import * as React from "react";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { values } from "lodash";

import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { useSelector } from "@teamprevent/admin-redux";

import { ContextAwareCheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/ContextAwareCheckboxQuestion";

import { ApplicationState } from "../../applicationState";

import { DataProtectionSectionOld } from "./DataProtectionSectionOld";

interface Props {
    index?: number;
    readonly: boolean;
    questionnaireKey: string;
}

const sectionId = "DataProtection";
const typeOfIdUsedId = "TypeOfIdUsed";
const wasIdConfirmedIdValue = "Identity confirmed";

const wasIdConfirmedQuestion = {
    id: "WasIdConfirmed",
    questionText: "Was the individual's identity confirmed using any formal form of Identification?"
};

export const DataProtectionSectionNew = (props: Props) => {
    const { index, readonly, questionnaireKey } = props;

    const questionnaires = useSelector((state: ApplicationState) => state.questionnaires);
    const questionnaire = questionnaires[questionnaireKey];
    const section = questionnaire && questionnaire.sections.find(s => s.id === sectionId);

    const getAnswerToQuestionWithId = React.useCallback((id: string) => {
        const questions = section ? values(section.questions) : [];
        if (questions) {
            const question = questions.find(q => q.id === id && q.sectionId === sectionId);
            if (question) {
                return question.answer;
            }
        }

        return undefined;
    }, [section]);

    return (
        <AccordionQuestionnaireSection
            index={index}
            title="Data Protection"
            id={sectionId}
        >
            <p>In line with the UK Data Protection Act, General Data Protection Regulations
                I am also required to confirm the following with you.</p>
            <CheckboxQuestion
                mandatory
                addAsteriskIfMandatory
                readonly={readonly}
                questionConfig={({
                    id: "ConsentStoreData",
                    // eslint-disable-next-line max-len
                    questionText: "Do you understand that Optima Health will be required to keep a clinical record of this consultation and that any personal and health related information that is recorded will be held on our secure online system"
                })}
                value="Consented"
            />
            <ContextAwareCheckboxQuestion
                mandatory
                preserveInitialQuestionText
                questionConfig={({
                    id: "RequestDataExplained",
                    // eslint-disable-next-line max-len
                    questionText: "Do you understand that you can request a copy of your clinical notes at any time? Our registered address can be found on our website, www.optimahealth.co.uk*"
                })}
                value="Explained"
            />
            <ContextAwareCheckboxQuestion
                mandatory
                preserveInitialQuestionText
                questionConfig={({
                    id: "ConfirmPrivacyStatementExplained",
                    // eslint-disable-next-line max-len
                    questionText: "Please note that you can access our full privacy policy on our website, which clearly explains how, where, and when your personal and health data will be processed in detail.*"
                })}
                value="Explained"
            />
            <CheckboxQuestion
                readonly={readonly}
                questionConfig={wasIdConfirmedQuestion}
                value={wasIdConfirmedIdValue}
            />
            {getAnswerToQuestionWithId(wasIdConfirmedQuestion.id) &&
                <>
                    <DropdownQuestion
                        mandatory={getAnswerToQuestionWithId(wasIdConfirmedQuestion.id) === wasIdConfirmedIdValue}
                        readonly={readonly}
                        questionConfig={({ id: typeOfIdUsedId, questionText: "If Yes please confirm the type of identification that was shown" })}
                        options={[
                            { value: "Passport", text: "Passport" },
                            { value: "Driving Licence", text: "Driving Licence" },
                            { value: "Company ID Card", text: "Company ID Card" },
                            { value: "National Identity Card", text: "National Identity Card" },
                            { value: "Other", text: "Other" }
                        ]}
                    />
                    {getAnswerToQuestionWithId(typeOfIdUsedId) === "Other" &&
                        <TextQuestion
                            mandatory
                            readonly={readonly}
                            questionConfig={({ id: "OtherIdUsed", questionText: "Please specify ID here" })}
                        />}
                </>}
        </AccordionQuestionnaireSection>
    );
};

const immunisationWorkflowImprovementsPt1 = getFeatureToggles()[FeatureToggleNames.ImmunisationWorkflowImprovementsPt1];

export const DataProtectionSection = immunisationWorkflowImprovementsPt1 ? DataProtectionSectionNew : DataProtectionSectionOld;
